import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiSave, FiChevronLeft, FiPlus, FiX, FiImage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import productService from '../../services/product.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ImageUpload from '../../components/common/ImageUpload';
import VariantEditor from '../../components/product/VariantEditor';

const CATEGORIES = [
  'Electronics', 'Clothing', 'Home & Garden', 'Sports', 'Books',
  'Health & Beauty', 'Toys', 'Automotive', 'Food & Beverages',
  'Art & Crafts', 'Jewelry', 'Pet Supplies', 'Office', 'Other',
];

const defaultForm = {
  name: '',
  description: '',
  shortDescription: '',
  sku: '',
  price: '',
  compareAtPrice: '',
  costPrice: '',
  category: '',
  subcategory: '',
  tags: [],
  status: 'draft',
  stock: {
    quantity: 0,
    trackInventory: true,
    lowStockThreshold: 10,
    allowBackorder: false,
  },
  images: [],
  options: [],
  variants: [],
  hasVariants: false,
  shipping: {
    weight: '',
    freeShipping: false,
    dimensions: { length: '', width: '', height: '' },
  },
  seo: {
    metaTitle: '',
    metaDescription: '',
  },
};

const FarmerProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');

  useEffect(() => {
    if (isEdit) {
      const fetchProduct = async () => {
        try {
          const { data } = await productService.getById(id);
          const p = data.data;
          setForm({
            name: p.name || '',
            description: p.description || '',
            shortDescription: p.shortDescription || '',
            sku: p.sku || '',
            price: p.price || '',
            compareAtPrice: p.compareAtPrice || '',
            costPrice: p.costPrice || '',
            category: p.category || '',
            subcategory: p.subcategory || '',
            tags: p.tags || [],
            status: p.status || 'draft',
            stock: {
              quantity: p.stock?.quantity || 0,
              trackInventory: p.stock?.trackInventory !== false,
              lowStockThreshold: p.stock?.lowStockThreshold || 10,
              allowBackorder: p.stock?.allowBackorder || false,
            },
            images: p.images || [],
            options: p.options || [],
            variants: p.variants || [],
            hasVariants: p.hasVariants || false,
            shipping: {
              weight: p.shipping?.weight || '',
              freeShipping: p.shipping?.freeShipping || false,
              dimensions: {
                length: p.shipping?.dimensions?.length || '',
                width: p.shipping?.dimensions?.width || '',
                height: p.shipping?.dimensions?.height || '',
              },
            },
            seo: {
              metaTitle: p.seo?.metaTitle || '',
              metaDescription: p.seo?.metaDescription || '',
            },
          });
        } catch (error) {
          toast.error('Product not found');
          navigate('/merchant/products');
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    }
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNestedChange = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleDimensionChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        dimensions: { ...prev.shipping.dimensions, [field]: value },
      },
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const addImageUrl = () => {
    const url = imageUrlInput.trim();
    if (url) {
      setForm((prev) => ({
        ...prev,
        images: [...prev.images, { url, alt: '', isPrimary: prev.images.length === 0 }],
      }));
      setImageUrlInput('');
    }
  };

  const removeImage = (index) => {
    setForm((prev) => {
      const images = prev.images.filter((_, i) => i !== index);
      // If removed primary, make first one primary
      if (images.length > 0 && !images.some((img) => img.isPrimary)) {
        images[0].isPrimary = true;
      }
      return { ...prev, images };
    });
  };

  const setPrimaryImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((img, i) => ({ ...img, isPrimary: i === index })),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.description || !form.price || !form.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        compareAtPrice: form.compareAtPrice ? parseFloat(form.compareAtPrice) : undefined,
        costPrice: form.costPrice ? parseFloat(form.costPrice) : undefined,
        stock: {
          ...form.stock,
          quantity: parseInt(form.stock.quantity) || 0,
          lowStockThreshold: parseInt(form.stock.lowStockThreshold) || 10,
        },
        shipping: {
          ...form.shipping,
          weight: form.shipping.weight ? parseFloat(form.shipping.weight) : undefined,
          dimensions: {
            length: form.shipping.dimensions.length ? parseFloat(form.shipping.dimensions.length) : undefined,
            width: form.shipping.dimensions.width ? parseFloat(form.shipping.dimensions.width) : undefined,
            height: form.shipping.dimensions.height ? parseFloat(form.shipping.dimensions.height) : undefined,
          },
        },
      };

      if (isEdit) {
        await productService.update(id, payload);
        toast.success('Product updated!');
      } else {
        await productService.create(payload);
        toast.success('Product created!');
      }
      navigate('/merchant/products');
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} product`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="section-padding bg-light">
      <div className="container fd-page-container" style={{ maxWidth: '960px' }}>
        <Link to="/merchant/products" className="text-decoration-none text-muted d-inline-flex align-items-center gap-1 mb-3">
          <FiChevronLeft /> Back to Products
        </Link>

        <h2 className="mb-4">{isEdit ? 'Edit Product' : 'Add New Product'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="row g-4">
            {/* Left Column */}
            <div className="col-lg-8">
              {/* Basic Info */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Basic Information</h5>

                <div className="mb-3">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text" name="name" className="form-control"
                    value={form.name} onChange={handleChange}
                    placeholder="e.g. Wireless Bluetooth Headphones" required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Short Description</label>
                  <input
                    type="text" name="shortDescription" className="form-control"
                    value={form.shortDescription} onChange={handleChange}
                    placeholder="Brief one-liner" maxLength={500}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Full Description *</label>
                  <textarea
                    name="description" className="form-control" rows={6}
                    value={form.description} onChange={handleChange}
                    placeholder="Detailed product description..." required
                  />
                </div>
              </div>

              {/* Images */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">
                  <FiImage className="me-2" />Images
                </h5>

                {/* File Upload */}
                <ImageUpload
                  images={form.images}
                  onChange={(imgs) => setForm((prev) => ({ ...prev, images: imgs }))}
                  max={8}
                />

                {/* URL Input (alternative) */}
                <div className="mt-3 pt-3 border-top">
                  <small className="text-muted d-block mb-2">Or add by URL:</small>
                  <div className="input-group input-group-sm">
                    <input
                      type="url" className="form-control"
                      placeholder="Paste image URL..."
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImageUrl())}
                    />
                    <button type="button" className="btn btn-outline-primary" onClick={addImageUrl}>
                      <FiPlus /> Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Pricing</h5>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Price ($) *</label>
                    <input
                      type="number" name="price" className="form-control" step="0.01" min="0"
                      value={form.price} onChange={handleChange} required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Compare at Price ($)</label>
                    <input
                      type="number" name="compareAtPrice" className="form-control" step="0.01" min="0"
                      value={form.compareAtPrice} onChange={handleChange}
                    />
                    <small className="text-muted">Original price for showing discounts</small>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Cost Price ($)</label>
                    <input
                      type="number" name="costPrice" className="form-control" step="0.01" min="0"
                      value={form.costPrice} onChange={handleChange}
                    />
                    <small className="text-muted">For profit tracking (private)</small>
                  </div>
                </div>
              </div>

              {/* Variants */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Product Variants</h5>
                <VariantEditor
                  options={form.options}
                  variants={form.variants}
                  basePrice={parseFloat(form.price) || 0}
                  onChange={({ options, variants, hasVariants }) => {
                    setForm(prev => ({ ...prev, options, variants, hasVariants }));
                  }}
                />
              </div>

              {/* Inventory */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Inventory</h5>
                <div className="mb-3">
                  <label className="form-label">SKU</label>
                  <input
                    type="text" name="sku" className="form-control"
                    value={form.sku} onChange={handleChange}
                    placeholder="e.g. WBH-001"
                  />
                </div>
                <div className="form-check mb-3">
                  <input
                    type="checkbox" className="form-check-input" id="trackInventory"
                    checked={form.stock.trackInventory}
                    onChange={(e) => handleNestedChange('stock', 'trackInventory', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="trackInventory">Track inventory</label>
                </div>
                {form.stock.trackInventory && (
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Stock Quantity</label>
                      <input
                        type="number" className="form-control" min="0"
                        value={form.stock.quantity}
                        onChange={(e) => handleNestedChange('stock', 'quantity', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Low Stock Alert</label>
                      <input
                        type="number" className="form-control" min="0"
                        value={form.stock.lowStockThreshold}
                        onChange={(e) => handleNestedChange('stock', 'lowStockThreshold', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <div className="form-check">
                        <input
                          type="checkbox" className="form-check-input" id="allowBackorder"
                          checked={form.stock.allowBackorder}
                          onChange={(e) => handleNestedChange('stock', 'allowBackorder', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="allowBackorder">Allow backorder</label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Shipping */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Shipping</h5>
                <div className="form-check mb-3">
                  <input
                    type="checkbox" className="form-check-input" id="freeShipping"
                    checked={form.shipping.freeShipping}
                    onChange={(e) => handleNestedChange('shipping', 'freeShipping', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="freeShipping">Free shipping</label>
                </div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Weight (g)</label>
                    <input
                      type="number" className="form-control" min="0"
                      value={form.shipping.weight}
                      onChange={(e) => handleNestedChange('shipping', 'weight', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Length (cm)</label>
                    <input type="number" className="form-control" min="0"
                      value={form.shipping.dimensions.length}
                      onChange={(e) => handleDimensionChange('length', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Width (cm)</label>
                    <input type="number" className="form-control" min="0"
                      value={form.shipping.dimensions.width}
                      onChange={(e) => handleDimensionChange('width', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Height (cm)</label>
                    <input type="number" className="form-control" min="0"
                      value={form.shipping.dimensions.height}
                      onChange={(e) => handleDimensionChange('height', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* SEO */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">SEO</h5>
                <div className="mb-3">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text" className="form-control"
                    value={form.seo.metaTitle}
                    onChange={(e) => handleNestedChange('seo', 'metaTitle', e.target.value)}
                    placeholder="Page title for search engines"
                    maxLength={70}
                  />
                  <small className="text-muted">{form.seo.metaTitle.length}/70 characters</small>
                </div>
                <div className="mb-0">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    className="form-control" rows={2}
                    value={form.seo.metaDescription}
                    onChange={(e) => handleNestedChange('seo', 'metaDescription', e.target.value)}
                    placeholder="Brief description for search results"
                    maxLength={160}
                  />
                  <small className="text-muted">{form.seo.metaDescription.length}/160 characters</small>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="col-lg-4">
              {/* Status */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Status</h5>
                <select
                  name="status" className="form-select"
                  value={form.status} onChange={handleChange}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active (Published)</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Category */}
              <div className="dashboard-card mb-4">
                <h5 className="mb-3">Organization</h5>
                <div className="mb-3">
                  <label className="form-label">Category *</label>
                  <select
                    name="category" className="form-select"
                    value={form.category} onChange={handleChange} required
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Subcategory</label>
                  <input
                    type="text" name="subcategory" className="form-control"
                    value={form.subcategory} onChange={handleChange}
                    placeholder="e.g. Wireless, Running Shoes"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="form-label">Tags</label>
                  <div className="input-group mb-2">
                    <input
                      type="text" className="form-control" placeholder="Add a tag"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <button type="button" className="btn btn-outline-secondary" onClick={addTag}>
                      <FiPlus size={14} />
                    </button>
                  </div>
                  {form.tags.length > 0 && (
                    <div className="d-flex flex-wrap gap-1">
                      {form.tags.map((tag) => (
                        <span key={tag} className="badge bg-primary d-flex align-items-center gap-1">
                          {tag}
                          <FiX size={12} style={{ cursor: 'pointer' }} onClick={() => removeTag(tag)} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Save Actions */}
              <div className="dashboard-card">
                <button type="submit" className="btn btn-fd-primary w-100 btn-lg mb-2" disabled={saving}>
                  <FiSave className="me-2" />
                  {saving ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
                </button>
                {isEdit && (
                  <Link to="/merchant/products" className="btn btn-outline-secondary w-100">
                    Cancel
                  </Link>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FarmerProductFormPage;
